/**
 * Help (docs/help/manual): the window that opens on the chapter for the page
 * you are on (Help in the sidebar, or the ? key), with search, and links to the
 * full manual and its PDF. Admin parts show only to admins.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { MANUAL } from "../help/manual.generated";
import type { HelpChapter } from "../help/types";
import { useStore } from "../lib/store";
import { Modal } from "./ui";

export const MANUAL_PDF = "/help/Agora-Manual.pdf";

/** The chapters (and sections) this person may read. */
export function useManual(): HelpChapter[] {
  const admin = useStore((s) => s.boot?.workspace.role === "admin");
  return useMemo(
    () =>
      MANUAL.filter((c) => admin || c.audience !== "admin").map((c) => ({
        ...c,
        sections: c.sections.filter((s) => admin || s.audience !== "admin"),
      })),
    [admin],
  );
}

/** The chapter for a page: the most precise route that matches (path, then #hash). */
export function chapterFor(chapters: HelpChapter[], pathname: string, hash: string): string {
  let best = { id: "start", score: -1 };
  for (const c of chapters) {
    for (const r of c.routes) {
      const [path = "", tag] = r.split("#");
      const pathOk = path === "/" ? pathname === "/" : pathname === path || (path.endsWith("/") && pathname.startsWith(path));
      if (!pathOk || (tag && hash !== `#${tag}`)) continue;
      const score = path.length + (tag ? 100 : 0);
      if (score > best.score) best = { id: c.id, score };
    }
  }
  return best.id;
}

function search(chapters: HelpChapter[], q: string) {
  const words = q.toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  if (!words.length) return [];
  const hits: { chapter: HelpChapter; section: HelpChapter["sections"][number]; score: number }[] = [];
  for (const c of chapters)
    for (const s of c.sections) {
      const hay = `${c.title} ${s.title} ${s.text}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) continue;
      hits.push({ chapter: c, section: s, score: words.reduce((n, w) => n + (s.title.toLowerCase().includes(w) ? 3 : 0) + (c.title.toLowerCase().includes(w) ? 2 : 0), 0) });
    }
  return hits.sort((a, b) => b.score - a.score).slice(0, 12);
}

/** One chapter's sections; links inside the manual stay in the window, pictures open full size. */
export function ChapterBody({ chapter, onLink, onZoom }: { chapter: HelpChapter; onLink?: (target: string) => void; onZoom?: (src: string) => void }) {
  return (
    <div
      className="help-chapter"
      onClick={(e) => {
        const el = e.target as HTMLElement;
        const link = el.closest<HTMLAnchorElement>("a[data-help-link]");
        if (link && onLink) {
          e.preventDefault();
          onLink(link.dataset.helpLink!);
          return;
        }
        if (el instanceof HTMLImageElement && onZoom) onZoom(el.src);
      }}
    >
      <h2>{chapter.title}</h2>
      <p className="help-summary">{chapter.summary}</p>
      {chapter.sections.map((s) => (
        <section key={s.id} id={`${chapter.id}-${s.id}`} className="help-section">
          <h3>
            {s.title}
            {s.audience === "admin" && <span className="chip">admins</span>}
          </h3>
          <div dangerouslySetInnerHTML={{ __html: s.html }} />
        </section>
      ))}
    </div>
  );
}

export function HelpPanel({ onClose }: { onClose: () => void }) {
  const chapters = useManual();
  const [current, setCurrent] = useState(() => chapterFor(chapters, location.pathname, location.hash));
  const [q, setQ] = useState("");
  const [zoom, setZoom] = useState<string | null>(null);
  const body = useRef<HTMLDivElement>(null);
  const hits = useMemo(() => search(chapters, q), [chapters, q]);
  const chapter = chapters.find((c) => c.id === current) ?? chapters[0]!;
  // Big by default; drag the corner to resize; the size is remembered on this device.
  const sized = (el: HTMLDivElement | null) => {
    if (!el || el.dataset.sized) return;
    el.dataset.sized = "1";
    try {
      const saved = JSON.parse(localStorage.getItem("agora.helpSize") ?? "null") as { w: number; h: number } | null;
      if (saved && saved.w > 400 && saved.h > 300) {
        el.style.width = `${Math.min(saved.w, innerWidth - 16)}px`;
        el.style.height = `${Math.min(saved.h, innerHeight - 16)}px`;
      }
    } catch {
      // no saved size
    }
    let t: ReturnType<typeof setTimeout> | undefined;
    new ResizeObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => {
        try {
          localStorage.setItem("agora.helpSize", JSON.stringify({ w: el.offsetWidth, h: el.offsetHeight }));
        } catch {
          // storage blocked
        }
      }, 300);
    }).observe(el);
  };
  const go = (target: string) => {
    const [c, s] = target.split("/");
    setQ("");
    setCurrent(c!);
    setTimeout(() => (s ? document.getElementById(`${c}-${s}`)?.scrollIntoView({ block: "start" }) : body.current?.scrollTo({ top: 0 })), 0);
  };
  return (
    <Modal title="Help" onClose={onClose} className="help-modal" modalRef={sized}>
      <div className="help-window">
        <nav className="help-nav" aria-label="Help chapters">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the help" aria-label="Search the help" autoFocus />
          {q.trim() ? (
            <ul>
              {hits.length === 0 && <li className="muted-text small">Nothing found.</li>}
              {hits.map((h) => (
                <li key={`${h.chapter.id}/${h.section.id}`}>
                  <button type="button" onClick={() => go(`${h.chapter.id}/${h.section.id}`)}>
                    <b>{h.section.title}</b>
                    <small>{h.chapter.title}</small>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <ul>
              {chapters.map((c) => (
                <li key={c.id}>
                  <button type="button" className={c.id === chapter.id ? "on" : ""} onClick={() => go(c.id)}>
                    {c.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="help-links">
            <Link to={`/help?c=${chapter.id}`} onClick={onClose}>
              Full manual
            </Link>
            <a href={MANUAL_PDF} target="_blank" rel="noreferrer">
              PDF
            </a>
          </div>
        </nav>
        <div className="help-body" ref={body}>
          <ChapterBody chapter={chapter} onLink={go} onZoom={setZoom} />
        </div>
      </div>
      {zoom && (
        <button type="button" className="help-zoom" onClick={() => setZoom(null)} aria-label="Close the picture">
          <img src={zoom} alt="" />
        </button>
      )}
    </Modal>
  );
}

/** The ? key opens the help, unless you are typing. */
export function useHelpKey(open: () => void): void {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      open();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}
